import { DatePipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EventParticipationAnswerModel } from 'src/app/models/event-participations/event-participation-answer.model';
import { EventParticipationQuestionModel } from 'src/app/models/event-participations/event-participation-question.model';
import { ParticipationQuestionTypeModel } from 'src/app/models/event-participations/participation-question-type.model';
import { EventMemberStatusModel } from 'src/app/models/events/event-member-status.model';
import { EventMemberModel } from 'src/app/models/events/event-member.model';
import { TableColumn } from 'src/app/models/table-column.model';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
import { AdditionalParticipantModel } from 'src/app/models/event-participations/additional-participant.model';

interface QuestionAccordionItem {
  question: EventParticipationQuestionModel;
  columns: TableColumn[];
  answers: EventParticipationAnswerModel[];

  acceptedAnswers: EventParticipationAnswerModel[];
  pendingAnswers: EventParticipationAnswerModel[];
  
  acceptedColumns: TableColumn[];
  pendingColumns: TableColumn[];

  showTabs: boolean;
  selectedTab?: any;
  tabs: any[];
}

@Component({
  selector: 'app-training-participation-answers',
  standalone: false,
  templateUrl: './training-participation-answers.component.html',
  styleUrl: './training-participation-answers.component.scss',
})
export class TrainingParticipationAnswersComponent {
  @Input() questions: EventParticipationQuestionModel[] = [];

  @Input()
  allMembers: EventMemberModel[] = [];

  accordionItems: QuestionAccordionItem[] = [];

  questionTypes = ParticipationQuestionTypeModel;

  constructor(
    private translateService: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.questions.forEach(q => {
      if(q.translationKey) {
        q.question = this.translateService.instant(q.translationKey);

        q.options?.forEach(o => {
          if(o.translationKey) {
            o.value = this.translateService.instant(o.translationKey);
          }
        });
      }

      q.answers.forEach(a => {
        a.selectedOptions?.forEach(o => {
          if(o.translationKey) {
            o.value = this.translateService.instant(o.translationKey);
          }
        });
      })
    });

    this.setAdditionalParticipants();
    this.buildAccordion();
  }

  private personColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.LAST-NAME'),
      dataField: 'person.lastName',
      cellTemplate: 'nameParticipantsTemplate',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.FIRST-NAME'),
      dataField: 'person.firstName'
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.EMAIL'),
      dataField: 'person.email'
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.EMPLOYEE-ID'),
      dataField: 'person.agentNumber'
    },
  ];

  private pendingStatusColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.STATUS'),
      dataField: '',
      cellValue: (data: EventParticipationAnswerModel): any => {
        const member = this.allMembers.find(m => m.person.id == data.person.id);
        if(!member) return false;
        return this.getStatusText(member.status);
      },
    },
  ];

  private freeTextColumns: TableColumn[] = [
    ...this.personColumns,
    {
      caption: this.translateService.instant('TRAINING-QUESTIONNAIRE.PARTICIPATION.ANSWER'),
      dataField: 'freeTextAnswer'
    }
  ];

  private yesNoColumns: TableColumn[] = [
    ...this.personColumns,
    {
      dataField: '',
      caption: this.translateService.instant('TRAINING-QUESTIONNAIRE.PARTICIPATION.ANSWER'),
      calculateDisplayValue: (data: EventParticipationAnswerModel) => {
        return data.yesNoAnswer
          ? this.translateService.instant('COMMON.YES')
          : this.translateService.instant('COMMON.NO')
      }
    }
  ];

  private selectOptionColumns: TableColumn[] = [
    ...this.personColumns,
    {
      dataField: 'selectedOptions',
      caption: this.translateService.instant('TRAINING-QUESTIONNAIRE.PARTICIPATION.ANSWER'),
      cssClass: 'width-350',
      headerFilter: (data: any) => {
        data.dataSource.postProcess = (results: any) => {
          const selectedOptionNames = results.reduce(
            (a: any, b: any) => a.concat(b.value),
            []
          );
          const parsedSelectedOptionNames: string[] = [];
          selectedOptionNames.forEach((option: string) => {
            if (!option || !option.includes(', ')) {
              parsedSelectedOptionNames.push(option);
            } else if (option.includes(', ')) {
              const options = option.split(', ');
              parsedSelectedOptionNames.push(...options);
            }
          });
          const uniqueSelectedOptionNames = [
            ...new Set(parsedSelectedOptionNames),
          ];

          results.splice(0, results.length);
          uniqueSelectedOptionNames.forEach((tgName) => {
            if (tgName) {
              results.push({
                text: tgName,
                value: tgName,
              });
            } else {
              results.push({
                text: '(Blanks)',
                value: null,
              });
            }
          });
          return results;
        };
      },
      filterExpression: (value: any) => {
        function getSelectedOptionsFilterString(rowData: EventParticipationAnswerModel) {
          if(!rowData.selectedOptions) return '';
          const options = rowData.selectedOptions.map(option => option.value);
          return !!options.join(',') ? options.join(',') + ',' : 'empty';
        }

        return [
          [
            getSelectedOptionsFilterString,
            'contains',
            value ? value + ',' : 'empty',
          ],
        ];
      },
      cellValue: (data: EventParticipationAnswerModel | any): string => {
        return data.selectedOptions.map((option: any) => option.value).join(', ');
      },
      calculateDisplayValue: (data: EventParticipationAnswerModel | any): string => {
        return data.selectedOptions.map((option: any) => option.value).join(', ');
      },
    }
  ];

  private additionalPersonColumns: TableColumn[] = [
    ...this.personColumns
  ];

  private hotelColumns: TableColumn[] = [
    ...this.personColumns,
    {
      dataField: 'startHotelDate',
      caption: this.translateService.instant('TRAINING-QUESTIONNAIRE.PARTICIPATION.DATE-RANGE'),
      calculateDisplayValue: (data: EventParticipationAnswerModel) => {
        if (!data.startHotelDate || !data.endHotelDate) return '';

        const start = new DatePipe(this.translateService.currentLang)
          .transform(data.startHotelDate, 'dd.MM.yyyy');

        const end = new DatePipe(this.translateService.currentLang)
          .transform(data.endHotelDate, 'dd.MM.yyyy');

        return `${start} - ${end}`;
      },
      cellValue: (data: EventParticipationAnswerModel): any => {
        if(!data.startHotelDate) return null;
        return new Date(data.startHotelDate);
      },
      calculateSortValue: (data: EventParticipationAnswerModel): Date | string => {
        if (!data.startHotelDate) {
          return '';
        }

        return new Date(data.startHotelDate);
      },
    },
  ];

  private statusColumn: TableColumn = {
    dataField: '__status',
    caption: this.translateService.instant('COMMON.FIELDS.STATUS'),
    calculateDisplayValue: (data: EventParticipationAnswerModel) => {
      const member = this.allMembers.find(
        m => m.person.id === data.person.id
      );
      if (!member) return '';
      return this.getStatusText(member.status);
    }
  };

  private setAdditionalParticipants(): void {
   const map = new Map<string, AdditionalParticipantModel[]>();

    for (const question of this.questions) {
      for (const answer of question.answers ?? []) {
        const key = answer.person.id;
        if (!key) continue;

        if (answer.additionalParticipants?.length) {
          map.set(
            key,
            answer.additionalParticipants
          );
        }
      }
    }

    this.questions.forEach(q => {
      if(q.type == ParticipationQuestionTypeModel.HotelBooking) {
        q.answers.forEach(a => {
          a.additionalParticipants = map.get(a.person.id) ?? [];
        });
      }
    });
  }

  buildAccordion(): void {
    this.accordionItems = this.questions.map(question => {
      let columns: TableColumn[];

      switch (question.type) {
        case ParticipationQuestionTypeModel.FreeText:
          columns = this.freeTextColumns;
          break;
        case ParticipationQuestionTypeModel.YesNo:
          columns = this.yesNoColumns;
          break;
        case ParticipationQuestionTypeModel.SingleSelect:
        case ParticipationQuestionTypeModel.MultiSelect:
          columns = this.selectOptionColumns;
          break;
        case ParticipationQuestionTypeModel.AdditionalPersons:
          columns = this.additionalPersonColumns;
          break;
        case ParticipationQuestionTypeModel.HotelBooking:
          columns = this.hotelColumns;
          break;
        default:
          columns = this.personColumns;
      }

      const filteredAnswers = question.answers?.filter(answer => {
        switch (question.type) {
          case ParticipationQuestionTypeModel.HotelBooking:
            return answer.startHotelDate && answer.endHotelDate;
          case ParticipationQuestionTypeModel.SingleSelect:
          case ParticipationQuestionTypeModel.MultiSelect:
            return answer.selectedOptions && answer.selectedOptions.length > 0;
          case ParticipationQuestionTypeModel.FreeText:
            return !!answer.freeTextAnswer;
          case ParticipationQuestionTypeModel.YesNo:
            return answer.yesNoAnswer !== null && answer.yesNoAnswer !== undefined;
          case ParticipationQuestionTypeModel.AdditionalPersons:
            return answer.additionalParticipants && answer.additionalParticipants.length > 0;
          default:
            return true;
        }
      }) ?? [];

      const { accepted, pending } = this.splitAnswers(filteredAnswers);
      const tabs = this.buildTabs(question.type, accepted, pending);

      return {
        question,
        columns,
        answers: filteredAnswers,
        acceptedAnswers: accepted,
        pendingAnswers: pending,
        acceptedColumns: columns,
        pendingColumns: [...columns, ...this.pendingStatusColumns],
        showTabs: accepted.length > 0 && pending.length > 0,
        selectedTab: accepted.length > 0 ? tabs[0] : tabs[1],
        tabs: tabs
      };
    });
  }

  getTotalCount(question: QuestionAccordionItem): number {
    const { acceptedCount, pendingCount } = this.getCounts(
      question.question.type, question.acceptedAnswers, question.pendingAnswers
    );
    const totalCount = acceptedCount + pendingCount;
    return totalCount;
  }

  private buildTabs(
    questionType: ParticipationQuestionTypeModel, 
    accepted: EventParticipationAnswerModel[], 
    pending: EventParticipationAnswerModel[]
  ) {
    const { acceptedCount, pendingCount } = this.getCounts(questionType, accepted, pending);

    return [
      {
        key: 'accepted',
        text: `${this.translateService.instant('TRAINING-QUESTIONNAIRE.PARTICIPATION.TABS.ACCEPTED')} (${acceptedCount})`
      },
      {
        key: 'pending',
        text: `${this.translateService.instant('TRAINING-QUESTIONNAIRE.PARTICIPATION.TABS.PENDING')} (${pendingCount})`
      }
    ];
  }

  private getCounts(
    questionType: ParticipationQuestionTypeModel,
    accepted: EventParticipationAnswerModel[],
    pending: EventParticipationAnswerModel[]
  ): { acceptedCount: number; pendingCount: number } {

    let acceptedCount = 0;
    let pendingCount = 0;

    if (
      questionType === ParticipationQuestionTypeModel.AdditionalPersons ||
      questionType === ParticipationQuestionTypeModel.HotelBooking
    ) {
      const acceptedAdditional = accepted.reduce(
        (sum, member) => sum + (member.additionalParticipants?.length ?? 0),
        0
      );

      const pendingAdditional = pending.reduce(
        (sum, member) => sum + (member.additionalParticipants?.length ?? 0),
        0
      );

      acceptedCount = acceptedAdditional + accepted.length;
      pendingCount = pendingAdditional + pending.length;
    } else {
      acceptedCount = accepted.length;
      pendingCount = pending.length;
    }

    return { acceptedCount, pendingCount };
  }

  private splitAnswers(answers: EventParticipationAnswerModel[]) {
    const accepted: EventParticipationAnswerModel[] = [];
    const pending: EventParticipationAnswerModel[] = [];

    answers.forEach(a => {
      const member = this.allMembers.find(m => m.person.id === a.person.id);
      if (!member) return;

      switch (member.status) {
        case EventMemberStatusModel.Accepted:
        case EventMemberStatusModel.Completed:
        case EventMemberStatusModel.Open:
          accepted.push(a);
          break;

        case EventMemberStatusModel.Waiting:
        case EventMemberStatusModel.Requested:
          pending.push(a);
          break;
      }
    });

    return { accepted, pending };
  } 

  rowStyleCallback = (row: any) => {
    const member = this.allMembers.find(m => m.person.id == row.person.id);
    if(!member) return null;
    return (
      member.status === EventMemberStatusModel.Accepted || 
      member.status === EventMemberStatusModel.Completed ||
      member.status === EventMemberStatusModel.Open
    ) ? null : 'gray-row';
  }

  hasAdditionalParticipants = (row: any) => row.additionalParticipants?.length > 0;

  getStatusText(status: EventMemberStatusModel): string {
    switch (status) {
      case EventMemberStatusModel.Waiting:
        return this.translateService.instant('COMMON.FIELDS.EMPLOYEE-STATUSES.WAITLIST')?.toLocaleLowerCase();

      case EventMemberStatusModel.Requested:
        return this.translateService.instant('COMMON.FIELDS.EMPLOYEE-STATUSES.REQUESTED')?.toLocaleLowerCase();

      default:
        return '';
    }
  };

  getAdditionalRows = (row: EventParticipationAnswerModel) => {
    return row.additionalParticipants ?? [];
  };

  mapAdditionalRow = (p: any) => {
    return [
      `${p.lastName ?? ''}`.trim(),
      `${p.firstName ?? ''}`.trim(),
      ''  // Email column empty
    ];
  };

  getShowRowDetails(questionType: ParticipationQuestionTypeModel) {
    return questionType ===  ParticipationQuestionTypeModel.AdditionalPersons
      || questionType === ParticipationQuestionTypeModel.HotelBooking;
  }
  
  async exportAll(): Promise<void> {
    const workbook = new Workbook();

    for (const item of this.accordionItems) {
      this.exportQuestionSheet(workbook, item);
    }

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], { type: 'application/octet-stream' }),
      `questionnaire.xlsx`
    );
  }

  private exportQuestionSheet(workbook: Workbook, item: QuestionAccordionItem): void {
    const worksheet = workbook.addWorksheet(
      this.getSafeSheetName(item.question.question!)
    );

    const columns = item.columns.filter(
      (c: any) =>
        c.dataField ||
        c.calculateCellValue ||
        c.calculateDisplayValue ||
        c.lookup
    );

    if(item.acceptedAnswers.length && item.pendingAnswers.length) {
      columns.push(this.statusColumn);
    }

    const data = item.acceptedAnswers.concat(item.pendingAnswers);

    // =========================
    // HEADER
    // =========================
    const headerRow = worksheet.addRow(
      columns.map((c: any) => c.caption || c.dataField)
    );

    headerRow.eachCell(cell => {
      cell.font = { bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFFFF' }
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    worksheet.views = [{ state: 'frozen', ySplit: 1 }];

    worksheet.autoFilter = {
      from: 'A1',
      to: `${this.getColumnLetter(columns.length)}1`
    };

    // =========================
    // COLUMN WIDTHS
    // =========================
    columns.forEach((col: any, index: number) => {
      const header = col.caption || col.dataField || '';

      let maxLength = header.length;

      data.forEach((row: any) => {
        const value = this.getCellValueForExport(row, col);
        if (value != null) {
          maxLength = Math.max(maxLength, String(value).length);
        }
      });

      worksheet.getColumn(index + 1).width = Math.min(maxLength + 8, 80);
    });

    // =========================
    // DATA
    // =========================
    let rowOffset = 1;

    data.forEach((row: any) => {

      const mainRow = columns.map((col: any) =>
        this.getCellValueForExport(row, col)
      );

      const excelRow = worksheet.addRow(mainRow);
      rowOffset++;

      // =========================
      // STATUS COLORING
      // =========================
      const statusCell = excelRow.getCell(columns.length);
      const status = statusCell.value;

      if (
        status === this.translateService.instant('COMMON.FIELDS.EMPLOYEE-STATUSES.WAITLIST')?.toLocaleLowerCase() ||
        status === this.translateService.instant('COMMON.FIELDS.EMPLOYEE-STATUSES.REQUESTED')?.toLocaleLowerCase()
      ) {
        statusCell.font = { color: { argb: 'FF808080' } }; // gray
      }

      // =========================
      // ADDITIONAL ROWS
      // =========================
      if (this.getAdditionalRows) {
        const additional = this.getAdditionalRows(row) || [];

        additional.forEach((child: any) => {

          const values = this.mapAdditionalRow
            ? this.mapAdditionalRow(child)
            : [child?.name ?? ''];

          worksheet.insertRow(rowOffset + 1, values);
          rowOffset++;
        });
      }
    });
  }

  private getCellValueForExport(row: any, col: any): any {
    if (col.calculateDisplayValue) {
      return col.calculateDisplayValue(row);
    }

    if (col.calculateCellValue) {
      return col.calculateCellValue(row);
    }

    if (col.lookup && col.lookup.dataSource) {
      const rawValue = this.resolveValue(row, col.dataField);

      const match = col.lookup.dataSource.find(
        (x: any) => x[col.lookup.valueExpr] === rawValue
      );

      return match ? match[col.lookup.displayExpr] : rawValue;
    }

    return this.resolveValue(row, col.dataField);
  }

  private resolveValue(row: any, field: string): any {
    if (!field) return '';

    const value = field
      .split('.')
      .reduce((acc, key) => acc?.[key], row);

    if (Array.isArray(value)) {
      return value.map(v => v?.name ?? v).join(', ');
    }

    return value ?? '';
  }

  private getColumnLetter(colNum: number): string {
    let letter = '';
    while (colNum > 0) {
      const temp = (colNum - 1) % 26;
      letter = String.fromCharCode(temp + 65) + letter;
      colNum = (colNum - temp - 1) / 26;
    }
    return letter;
  }

  private getSafeSheetName(name: string): string {
    return name
      .replace(/[*?:\\/[\]]/g, '')
      .substring(0, 31) || 'Sheet';
  }
}
